import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Loader2, Search, UsersRound, X } from "lucide-react";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import UserInfo from "../../../interfaces/UserInfo";
import './CreateConversationModal.css';

interface Props {
  addConversationView: boolean;
  prepareResource?: boolean;
  resourceTitle?: string;
  users: UserInfo[];
  selectedUsers: string[];
  setSelectedUsers: (ids: string[]) => void;
  handleCreateConversation: () => void;
  isCreating?: boolean;
  onClose: () => void;
  chatClose: () => void;
  inline?: boolean;
  selectionMode?: "single" | "multiple";
  showOrganizations?: boolean;
}

const keepVisibleInList = (element: HTMLElement, list: HTMLElement) => {
    const item = element.getBoundingClientRect(); const viewport = list.getBoundingClientRect();
    if (item.bottom > viewport.bottom) list.scrollTop += item.bottom - viewport.bottom;
    else if (item.top < viewport.top) list.scrollTop -= viewport.top - item.top;
  };

export default function CreateConversationModal({
  users,
  prepareResource = false,
  resourceTitle,
  selectedUsers,
  setSelectedUsers,
  handleCreateConversation,
  isCreating = false,
  onClose,
  inline = false,
  selectionMode = "multiple",
  showOrganizations = false,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);
  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, []);
  const userListRef = useRef<HTMLDivElement | null>(null);
  const selectedListRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  const pendingChipFocus = useRef<{ id?: string } | null>(null);
  const pendingScrollTopRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (
      pendingScrollTopRef.current !== null &&
      userListRef.current
    ) {
      userListRef.current.scrollTop = pendingScrollTopRef.current;
      pendingScrollTopRef.current = null;
    }
    if (pendingChipFocus.current) {
      const target = chipRefs.current.get(pendingChipFocus.current.id ?? '') ?? searchRef.current;
      pendingChipFocus.current = null;
      target?.focus({ preventScroll: true });
      if (target && selectedListRef.current?.contains(target)) keepVisibleInList(target, selectedListRef.current);
    } else if (prepareResource && document.activeElement instanceof HTMLElement && userListRef.current?.contains(document.activeElement)) {
      keepVisibleInList(document.activeElement, userListRef.current);
    }
  }, [selectedUsers, prepareResource]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return users
      // TEMPORARY: Hide participant accounts from chat creation until
      // participant messaging is supported in the mobile app.
      .filter(
        (user) =>
          (user as UserInfo & { isParticipant?: boolean }).isParticipant !== true
      )
      .filter((user) => {
        const name = `${user.firstName ?? ""} ${user.lastName ?? ""} ${
          user.displayName ?? ""
        } ${user.email ?? ""} ${user.organization?.name ?? ""} ${
          user.organization?.abbreviation ?? ""
        }`.toLowerCase();

        return !term || name.includes(term);
      })
      .sort(
        (a, b) => prepareResource ? 0 :
          Number(selectedUsers.includes(b.id)) -
          Number(selectedUsers.includes(a.id))
      );
  }, [users, searchTerm, selectedUsers, prepareResource]);

  const toggleUser = (id: string) => {
    if (isCreating) return;
    // Ordinary chat sorts selected users first; resource sharing keeps rows
    // stable for keyboard navigation. Preserve each list's scroll position.
    if (userListRef.current) {
      pendingScrollTopRef.current = userListRef.current.scrollTop;
    }

    if (selectionMode === "single") {
      setSelectedUsers(selectedUsers.includes(id) ? [] : [id]);
      return;
    }

    setSelectedUsers(
      selectedUsers.includes(id)
        ? selectedUsers.filter((userId) => userId !== id)
        : [...selectedUsers, id]
    );
  };

  const content = (
    <div
      className={
        inline
          ? "chat-create-conversation flex h-full min-h-0 w-full flex-col bg-white dark:bg-slate-900"
          : "chat-create-conversation mx-5 flex max-h-[90vh] w-[448px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900 dark:shadow-black/40"
      }
    >
      <div
        className={`flex shrink-0 items-center gap-3 bg-white dark:bg-slate-900 ${
          inline ? "px-3 py-3" : "px-5 py-4"
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-0 bg-slate-100 text-slate-600 shadow-none transition-colors hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus-visible:bg-slate-200 focus-visible:outline-none focus-visible:ring-0 dark:bg-[#282c39] dark:text-slate-300 dark:hover:bg-[#323746] dark:hover:text-white dark:focus-visible:bg-[#323746]"
          aria-label={inline ? "Back to conversations" : "Close"}
        >
          {inline ? <ArrowLeft size={18} /> : <X size={18} />}
        </button>

        <div className="min-w-0 flex-1">
          <h2 className="m-0 truncate text-[15px] font-semibold text-slate-900 dark:text-slate-100">
            {prepareResource ? "New message" : selectionMode === "single" ? "New private message" : "Start new chat"}
          </h2>
          <p className="m-0 mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
            {selectionMode === "single"
              ? "Choose one person to start a conversation"
              : "Choose one or more people"}
          </p>
        </div>
      </div>

      {prepareResource && <div className="chat-resource-share-summary mx-3 mt-1 rounded-xl px-3 py-2 text-sm">
        <span className="chat-resource-share-summary__label block text-xs">Resource</span>
        <span className="chat-resource-share-summary__title block break-words font-medium">{resourceTitle || 'Resource'}</span>
      </div>}
      {prepareResource && selectedUsers.length > 0 && <div ref={selectedListRef} className="flex max-h-28 shrink-0 flex-wrap items-center gap-1.5 overflow-y-auto px-3 pb-1 pt-3" aria-label="Selected recipients">
        <span className="text-xs text-slate-500 dark:text-slate-400">To:</span>
        {selectedUsers.map(id => {
          const user = users.find(person => person.id === id);
          const name = user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || 'Selected recipient';
          return <button type="button" key={id} ref={element => { if (element) chipRefs.current.set(id, element); else chipRefs.current.delete(id); }} disabled={isCreating} aria-label={`Remove ${name}`}
            onClick={() => {
              const index = selectedUsers.indexOf(id);
              pendingChipFocus.current = { id: selectedUsers[index + 1] ?? selectedUsers[index - 1] };
              setSelectedUsers(selectedUsers.filter(selected => selected !== id));
            }}
            className="inline-flex max-w-full items-center gap-1 rounded-full border-0 bg-purple-100 px-2.5 py-1 text-xs text-purple-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-200">
            <span className="break-words">{name}</span><X size={12} className="shrink-0" aria-hidden="true" />
          </button>;
        })}
      </div>}
      <div className={`${inline ? "px-3 pt-3" : "px-5 pt-4"} shrink-0`}>
        <div className="flex items-center gap-2 rounded-xl border-0 bg-slate-100 px-3 shadow-none transition-colors focus-within:bg-slate-200 dark:bg-[#242835] dark:focus-within:bg-[#2b3040]">
          <Search size={16} className="shrink-0 text-slate-400 dark:text-slate-500" />
          <input
            className="w-full !border-0 !border-transparent bg-transparent py-2.5 text-[13px] text-slate-800 !outline-none !ring-0 !shadow-none placeholder:text-slate-400 focus:!border-0 focus:!border-transparent focus:!outline-none focus:!ring-0 focus:!shadow-none dark:text-slate-100 dark:placeholder:text-slate-500"
            ref={searchRef}
            aria-label="Search people"
            placeholder="Search people..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            autoFocus
          />
        </div>
      </div>

      <div
        ref={userListRef}
        className={`min-h-0 flex-1 overflow-y-auto scrollbar-thin ${inline ? "p-2" : "px-3 py-3"}`}
      >
        {filteredUsers.length === 0 ? (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center px-8 text-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-[#1d202b] dark:text-slate-500">
              <UsersRound size={20} />
            </div>
            <p className="m-0 text-sm font-semibold text-slate-700 dark:text-slate-200">No people found</p>
            <p className="mb-0 mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Try searching by a different name or email address.
            </p>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const selected = selectedUsers.includes(user.id);
            const displayName =
              user.displayName ||
              `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
              user.email ||
              "Unknown user";

            return (
              <button
                type="button"
                key={user.id}
                disabled={isCreating}
                aria-pressed={selected}
                onClick={(event) => {
                  // Preserve keyboard focus in the stable resource picker. Pointer
                  // selection keeps the regular picker's existing scroll behavior.
                  if (!prepareResource || event.detail > 0) event.currentTarget.blur();
                  toggleUser(user.id);
                }}
                className={`mb-1 flex w-full items-center gap-3 rounded-xl border-0 px-3 py-2.5 text-left shadow-none transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/40 ${
                  selected
                    ? "bg-[#7E57C2]/[0.10] dark:bg-purple-400/15"
                    : "bg-slate-50 hover:bg-slate-100 dark:bg-[#1d202b] dark:hover:bg-[#242835]"
                }`}
              >
                <div className="relative shrink-0">
                  <AvatarImage userId={user.id} size="xs" displayName={displayName} alternate />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-semibold text-slate-800 dark:text-slate-100">
                    {displayName}
                  </div>
                  {user.email && (
                    <div className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
                      {user.email}
                      {showOrganizations && user.organization?.name
                        ? ` · ${user.organization.name}`
                        : ""}
                    </div>
                  )}
                </div>

                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-0 transition-colors ${
                    selected
                      ? "bg-[#7E57C2] text-white"
                      : "bg-slate-200 text-transparent dark:bg-[#2c3140]"
                  }`}
                >
                  <Check size={13} strokeWidth={3} />
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className={`shrink-0 border-0 bg-slate-50 dark:bg-[#171a24] ${inline ? "p-3" : "px-5 py-4"}`}>
        <button
          type="button"
          disabled={selectedUsers.length === 0 || isCreating}
          onClick={handleCreateConversation}
          aria-busy={isCreating}
          className="chat-create-conversation-submit flex w-full items-center justify-center gap-2 rounded-xl bg-[#7E57C2] px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-[#6F49B5] disabled:cursor-not-allowed disabled:shadow-none dark:hover:bg-[#8f6bcb]"
        >
          {isCreating ? (
            <>
              <Loader2 size={15} className="animate-spin" aria-hidden="true" />
              <span>Starting chat...</span>
            </>
          ) : (
            prepareResource ? "Prepare message" : selectionMode === "single" ? "Start conversation" : "Start chat"
          )}
        </button>
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-slate-950/35 p-4 backdrop-blur-[1px] dark:bg-black/60">
      {content}
    </div>
  );
}
